import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

export interface MonthlySale { month: string; qty: number; revenue: number }
export interface SeasonPoint { month_num: number; avg_qty: number }
export interface SellThroughRow { abc_curve: 'A' | 'B' | 'C' | null; sold: number; stock: number }
export interface RepurchaseStats { avg_interval_days: number | null; median_interval_days: number | null; products_with_repurchase: number }
export interface SkuTrend { product_id: string; sku: string; name: string; first_half: number; second_half: number; change_pct: number | null }

export interface IntelligenceData {
  monthly: MonthlySale[]
  seasonality: SeasonPoint[]
  sellThrough: SellThroughRow[]
  repurchase: RepurchaseStats | null
  trend: SkuTrend[]
}

export function useIntelligence(windowMonths = 6) {
  const [data, setData] = useState<IntelligenceData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    const [monthlyRes, seasonRes, sellRes, repurchaseRes, trendRes] = await Promise.all([
      supabase.rpc('fn_monthly_sales', { p_months: 12 }),
      supabase.rpc('fn_seasonality', { p_months: 24 }),
      supabase.rpc('fn_sell_through', { p_months: windowMonths }),
      supabase.rpc('fn_repurchase_interval', { p_months: 12 }),
      supabase.rpc('fn_sku_trend', { p_months: windowMonths, p_limit: 30 }),
    ])

    const firstErr = monthlyRes.error ?? seasonRes.error ?? sellRes.error ?? repurchaseRes.error ?? trendRes.error
    if (firstErr) setError(firstErr.message)

    const repurchaseRow = Array.isArray(repurchaseRes.data) ? repurchaseRes.data[0] : repurchaseRes.data

    setData({
      monthly: (monthlyRes.data ?? []) as MonthlySale[],
      seasonality: (seasonRes.data ?? []) as SeasonPoint[],
      sellThrough: (sellRes.data ?? []) as SellThroughRow[],
      repurchase: (repurchaseRow as RepurchaseStats | undefined) ?? null,
      trend: (trendRes.data ?? []) as SkuTrend[],
    })
    setLoading(false)
  }, [windowMonths])

  useEffect(() => { fetchData() }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}
